import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, TableCell, TableRow, TextField, Typography } from "@mui/material";
import { ScoreEntry } from "common-fe";
import {
  PANEL_CONSENSUS_JUDGE_POSITION,
  DSHB_CLASS_KINDS,
  isDshbPlaceOnlyGridKind,
  isDshbScoredGridKind,
  resolveScoreSheetTotals,
  scoreStepFor,
} from "score-common";
import { useTranslation } from "i18n-fe";
import { useScoreSheet } from "../hooks/useScoreSheet";
import { focusNextGridCell } from "../util/classGridNav";

// #1750 — one row = one ride's own ScoreSheet, mounted independently. Every
// row calls useScoreSheet with its OWN rideId and the SAME fixed judge
// position (PANEL_CONSENSUS_JUDGE_POSITION, see score-common/constants.js
// for the #1748 rationale), which gives per-row isolation "by construction":
// each row's hook instance owns its own debounced autosave/outbox entry
// (entityKey `${rideId}::${judgeId}`), so a cell edit in one row can only
// ever write that row's own entityKey. Never lift sheet state to the parent.
//
// #1781 — `klass`/`test` are the SAME class/test ClassGrid already fetched
// (via useRidesForClass/useClassGridTest) for every row, passed through as
// useScoreSheet's preloaded params so this row skips its own getClass/getTest
// (and, via `ride` already being the fetched row, getRide too). This is safe
// specifically because ClassGrid builds ONE shared column set from `test` for
// every row in the table already — every row in a class-grid class is scored
// against that same test by construction, unlike the per-ride-test general
// case #462 guards against.
// #2183 — CHAMPIONSHIP-only place labels (DR210.5's own terminology: "a
// champion, reserve champion and third place reserve must be named").
// Anything past 3rd is a plain numeric place, same as every other class.
const CHAMPIONSHIP_PLACE_LABEL_KEYS = {
  1: "classGrid.championPlace",
  2: "classGrid.reserveChampionPlace",
  3: "classGrid.thirdPlaceReserve",
};

const EMPTY_SCORED_COLUMNS = [];

export default function ClassGridRow({
  ride,
  klass,
  test,
  dshbKind,
  observations,
  scoredColumns = EMPTY_SCORED_COLUMNS,
  registerRowEl,
  registerFirstInput,
  onTotalChange,
  onSheetStateChange,
  ceremonyOp,
  onCeremonyResult,
}) {
  const { t } = useTranslation("score");
  // #2183/#2184 — see ClassGrid's own doc comment: dshbKind is resolved
  // fresh by the parent off the CLASS's className, never off this reused
  // Test row. isDshbPlaceOnlyGridKind/isDshbScoredGridKind are score-common's
  // shared roots (dshb-class-kind.js) — see that file's own comment for why
  // this must not be re-derived inline (this is exactly the "coupled
  // component" trap: RideList/ClassGrid/ClassGridRow each had their own
  // copy of this check before #2184 centralized it).
  const isDshbPlaceOnlyGridClass = isDshbPlaceOnlyGridKind(dshbKind);
  const isDshbScoredGridClass = isDshbScoredGridKind(dshbKind);
  const isChampionshipGridClass = dshbKind === DSHB_CLASS_KINDS.CHAMPIONSHIP;
  const {
    sheet,
    loading: sheetLoading,
    isConfirmed,
    isCompleted,
    setSheetFields,
    setGeneralRemarks,
    setMovementScore,
    setCollective,
    complete,
    confirmMarks,
    signPanel,
  } = useScoreSheet(ride.id, PANEL_CONSENSUS_JUDGE_POSITION, { ride, klass, test });

  const marks = sheet?.observationMarks ?? {};

  // F5 (round-2 judge review) — a controlled type="number" input firing
  // Number(e.target.value) on every keystroke eats decimals mid-entry: Chrome
  // reports e.target.value === "" for an in-progress "72." (DSE half-marks,
  // dressage percentages), which used to route into the raw==="" branch —
  // deleting the mark / clearing percentage — and fire a debounced save
  // mid-keystroke. Keep the raw string in local draft state while typing and
  // only parse+save on blur/commit (native blur fires when
  // focusNextGridCell's Enter-as-Tab moves focus to the next cell, so
  // keyboard-only entry still saves on every cell it leaves).
  const [obsDrafts, setObsDrafts] = useState({});
  const [totalDraft, setTotalDraft] = useState(undefined);
  // #1752 — the Place column gets its own draft for the same reason Total
  // does: a controlled number input parsed on every keystroke loses the
  // in-progress value.
  const [placeDraft, setPlaceDraft] = useState(undefined);

  const commitObservation = (key, raw) => {
    const next = { ...marks };
    if (raw === "") {
      delete next[key];
    } else {
      const num = Number(raw);
      if (!Number.isNaN(num)) next[key] = num;
    }
    setSheetFields({ observationMarks: next });
    setObsDrafts((prev) => {
      if (!(key in prev)) return prev;
      const rest = { ...prev };
      delete rest[key];
      return rest;
    });
  };

  const commitTotal = (raw) => {
    if (raw === "") {
      // #1754 — `null` (not undefined) clears it, same contract commitPlace
      // below already uses: toBody() drops an `undefined` field from the
      // save body entirely (it means "not changing this field"), so an
      // admin/scribe clearing the Total used to see the cell go empty
      // locally while the old value silently came back on reload (#1750
      // final judge review, 2026-08-20 — "clearing a Total never clears it
      // server-side"). score-common's scoreSheetSchema.percentage is now
      // nullable to carry this explicit clear through to score-api.
      setSheetFields({ percentage: null });
    } else {
      const num = Number(raw);
      if (!Number.isNaN(num)) setSheetFields({ percentage: num });
    }
    setTotalDraft(undefined);
  };

  // #1752 — the judge's Place-column entry (ScoreSheet.place). Whole
  // positive numbers only: a place is an ordinal, and score-common's
  // suggestDressagePlacings rejects 0 rather than shifting it into a 1st
  // place, so writing a 0 here would store a value nothing downstream will
  // ever treat as a placing. `null` (not undefined) clears it, because an
  // omitted field is dropped from the save body and leaves the previous
  // value standing — see commitTotal above for the same contract.
  const commitPlace = (raw) => {
    if (raw === "") {
      setSheetFields({ place: null });
    } else {
      const num = Number(raw);
      if (Number.isInteger(num) && num > 0) setSheetFields({ place: num });
    }
    setPlaceDraft(undefined);
  };

  // #2184 — the live per-row DR211.4 total (Walk×3 + Trot×3 + Canter×3 +
  // General Impression×1, out of 100) for a SCORED grid class. This is the
  // SAME computation ScoringForm's own `totals` uses for the ordinary
  // per-ride movement grid (resolveScoreSheetTotals — falls straight
  // through to calculateScoreSheet's live math until the sheet is
  // confirmed/completed and freezes maxPoints, exactly the "in-progress
  // sheet" case every row here starts in). NOT read from sheet.percentage —
  // percentage is SERVER_MANAGED for every scoringModel except
  // "judge-percentage" (useScoreSheet's CLIENT_WRITABLE_TOTAL_SCORING_MODEL
  // guard), so it stays stale/undefined on this sheet until the scribe
  // confirms or completes it.
  const scoredTotals = useMemo(
    () => (isDshbScoredGridClass && test ? resolveScoreSheetTotals(sheet ?? {}, test) : null),
    [isDshbScoredGridClass, sheet, test],
  );

  // #2184 — "has a Total" for a scored grid row means every scored cell on
  // the row carries a real mark, the DR211.4 analog of DR133.1.j's "every
  // rider gets a final percentage": an empty cell means the printed sheet
  // isn't fully filled in yet, not that the (always-computable, even at 0)
  // percentage is somehow missing.
  const hasAllScoredMarks =
    scoredColumns.length > 0 &&
    scoredColumns.every((col) => {
      const entry =
        col.type === "movement"
          ? (sheet?.movementScores ?? []).find((m) => m.movementNumber === col.movementNumber)
          : sheet?.collectives?.[String(col.movementNumber)];
      return typeof entry?.score === "number" && Number.isFinite(entry.score);
    });

  const hasTotal = isDshbScoredGridClass ? hasAllScoredMarks : typeof sheet?.percentage === "number";
  const hasPlace = typeof sheet?.place === "number";

  // #2184 — writes a scored cell (movement or collective) through the same
  // per-row setMovementScore/setCollective ScoringForm's own movement grid
  // uses — same autosave/offline-outbox path as everything else on this
  // row, entirely separate from commitTotal above (which is judge-percentage
  // only and never called for a scored grid class).
  const commitScoredColumn = (col, score) => {
    if (col.type === "movement") {
      setMovementScore(col.movementNumber, { score });
    } else {
      setCollective(String(col.movementNumber), { score });
    }
  };

  // F4 (round-2 judge review) — report this row's live hasTotal up to
  // ClassGrid so the "still need a Total" banner counts the SHEET's own
  // percentage (the thing the scribe is actually typing into), not the
  // reconcile-stamped Ride.percentage cache, which never updates as the
  // scribe types on a fresh class. #1751's AC2 completion gate needs this
  // same per-row count, so report it from the one place that knows it.
  useEffect(() => {
    onTotalChange?.(ride.id, hasTotal);
  }, [onTotalChange, ride.id, hasTotal]);

  // #1751 — the row's ceremony state, reported up the same way hasTotal is.
  // Deliberately a SECOND channel rather than a widening of onTotalChange:
  // that one is #1750's DR133.1.j Total count and the confirm gate reads it,
  // so it stays exactly as it was.
  //
  // signedKey is a sorted, joined STRING, not the array, because this is an
  // effect dependency: `sheet.signatures.map(...)` is a fresh array identity on
  // every render and would re-fire this effect forever.
  const signedKey = (sheet?.signatures ?? [])
    .map((s) => s?.judgeId)
    .filter(Boolean)
    .sort()
    .join(",");
  // #2516 — carry the sheet's own noSignatureNeeded (#2406/#2407, stamped once
  // at createScoreSheet from the class's paperEntry flag) up to ClassCeremony
  // alongside the rest of the row's ceremony state. Read straight off THIS
  // row's sheet, never re-derived from klass.paperEntry, same rule #2412
  // already follows in ScoringForm.jsx.
  const noSignatureNeeded = Boolean(sheet?.noSignatureNeeded);
  useEffect(() => {
    onSheetStateChange?.(ride.id, {
      isConfirmed: Boolean(isConfirmed),
      isCompleted: Boolean(isCompleted),
      signedKey,
      noSignatureNeeded,
    });
  }, [onSheetStateChange, ride.id, isConfirmed, isCompleted, signedKey, noSignatureNeeded]);

  // #1751 — the class-level ceremony reaches a row by COMMANDING it, never by
  // the parent reaching into its sheet. Each row owns its own useScoreSheet
  // instance (see the header comment: per-row isolation by construction), so
  // "confirm the whole class" is 25 rows each running their own confirmMarks()
  // against their own entityKey, through the same offline outbox as everything
  // else. Lifting sheet state to the parent to do it centrally would undo that
  // isolation.
  //
  // `ranOpRef` makes each command run AT MOST ONCE per row: the mutators are
  // useCallback values that change identity as the sheet changes, so this
  // effect re-runs several times during a single op, and without the guard the
  // row would fire the same op again.
  //
  // There is deliberately NO cleanup that cancels the report. An earlier draft
  // had the usual `let cancelled = false` / `return () => { cancelled = true }`
  // pattern, and it deadlocked the ceremony: signPanel's own optimistic
  // setState changes the sheet, which changes persist's identity, which
  // changes signPanel's identity, which re-runs this effect WHILE the await is
  // still in flight. Cleanup then marked the in-flight closure cancelled, and
  // the re-run hit the ranOpRef guard and returned without reporting either —
  // so that row never reported at all, the parent's done-count never reached
  // the row count, and the busy dialog stayed up forever with the next stage's
  // buttons disabled. The report must ALWAYS fire; the guard, not the
  // cleanup, is what makes it fire exactly once.
  const ranOpRef = useRef(null);
  useEffect(() => {
    if (!ceremonyOp || ranOpRef.current === ceremonyOp.opId) return;
    ranOpRef.current = ceremonyOp.opId;
    (async () => {
      let error = null;
      try {
        if (ceremonyOp.kind === "confirm") {
          // Already frozen: nothing to do. The server treats a re-confirm as
          // an idempotent no-op too, so this is a saved round-trip, not a
          // correctness gate.
          if (!isConfirmed) await confirmMarks();
        } else if (ceremonyOp.kind === "sign") {
          await signPanel(ceremonyOp.judgeId);
        } else if (ceremonyOp.kind === "complete") {
          if (!isCompleted) await complete();
        }
      } catch (e) {
        error = e;
      }
      onCeremonyResult?.(ride.id, ceremonyOp.opId, error);
    })();
  }, [
    ceremonyOp,
    ride.id,
    isConfirmed,
    isCompleted,
    confirmMarks,
    signPanel,
    complete,
    onCeremonyResult,
  ]);

  // Round-6 judge review — these two MUST be stable across renders. React
  // calls a ref callback with null and then with the node again whenever the
  // callback's identity changes, so an inline arrow here re-registered both
  // refs on every keystroke (this row re-renders on each draft change), and
  // the unmount call that would clear the parent's entry was indistinguishable
  // from that churn. useCallback keyed on the ride makes the identity stable,
  // so the only null the parent ever sees is a real unmount — which is why
  // ClassGrid's registrars delete the entry on null instead of storing it.
  const registerRow = useCallback(
    (el) => registerRowEl?.(ride.id, el),
    [registerRowEl, ride.id],
  );
  const registerFirst = useCallback(
    (el) => registerFirstInput?.(ride.id, el),
    [registerFirstInput, ride.id],
  );

  return (
    <TableRow
      hover
      ref={registerRow}
      data-testid={`class-grid-row-${ride.competitorNumber ?? ride.id}`}
      // Round-6 judge review — every row loads its OWN sheet asynchronously,
      // so an EMPTY cell means one of two completely different things: "this
      // rider has no Total yet" or "this row hasn't loaded yet". They are
      // indistinguishable from the DOM, which is how AC10's evidence
      // screenshot came to be taken mid-load and still satisfy every
      // assertion in front of it. Publish the row's own settle state (same
      // convention as the offline toggle's data-sync-pending) so a test can
      // gate on hydration having FINISHED before it believes an empty cell.
      data-sheet-loaded={sheetLoading ? "false" : "true"}
      // #1751 — the row's own ceremony state, so a partially confirmed or
      // partially signed class is inspectable per row and not just as the
      // header's aggregate count.
      data-confirmed={isConfirmed ? "true" : "false"}
      data-completed={isCompleted ? "true" : "false"}
      data-signed-judges={signedKey}
    >
      {/* Sticky Rider No. column — the judge calls a number, not a name.
          Rider name is the row's SECONDARY label per DR133.1.g ("only the
          rider is judged"), never the horse. */}
      <TableCell
        sx={{
          position: "sticky",
          left: 0,
          zIndex: 1,
          bgcolor: "background.paper",
          borderRight: "1px solid",
          borderColor: "divider",
          minWidth: 96,
        }}
      >
        <Typography variant="body1" fontWeight={700}>
          {ride.competitorNumber ?? "—"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {ride.riderName ?? "—"}
        </Typography>
      </TableCell>

      {observations.map((obs, i) => (
        <TableCell key={obs.key} align="center">
          <TextField
            size="small"
            type="number"
            // #1751 / #696 — the class-level confirm freezes every row's
            // numeric entry at once. Notes below stay editable, same split as
            // the per-ride flow.
            disabled={Boolean(isConfirmed)}
            inputProps={{
              "data-grid-cell": "true",
              style: { width: 56, textAlign: "center" },
              "aria-label": t("classGrid.observationAriaLabel", {
                group: obs.group,
                label: obs.label,
                number: ride.competitorNumber ?? "",
              }),
            }}
            value={obs.key in obsDrafts ? obsDrafts[obs.key] : marks[obs.key] ?? ""}
            onChange={(e) =>
              setObsDrafts((prev) => ({ ...prev, [obs.key]: e.target.value }))
            }
            onBlur={(e) => commitObservation(obs.key, e.target.value)}
            onKeyDown={focusNextGridCell}
            inputRef={i === 0 ? registerFirst : undefined}
          />
        </TableCell>
      ))}

      {/* #2184 — a SCORED grid class's real marks: one cell per movement/
          collective the resolved test carries, each a REAL coefficient-
          weighted score (ScoreEntry — the SAME component ScoringForm's
          per-ride movement grid uses, coefficient badge and all, AC2's
          "coefficients visible"), never an unscored observation. Disabled
          the same way observations are, once the class-level confirm
          freezes the row. */}
      {isDshbScoredGridClass &&
        scoredColumns.map((col, i) => {
          const entry =
            col.type === "movement"
              ? (sheet?.movementScores ?? []).find((m) => m.movementNumber === col.movementNumber)
              : sheet?.collectives?.[String(col.movementNumber)];
          const cellKey = `${col.type}-${col.movementNumber}`;
          return (
            <TableCell key={cellKey} align="center">
              <ScoreEntry
                value={entry?.score}
                disabled={Boolean(isConfirmed)}
                coefficient={col.coefficient}
                step={scoreStepFor(test)}
                onChange={(score) => commitScoredColumn(col, score)}
                inputRef={i === 0 ? registerFirst : undefined}
                onKeyDown={focusNextGridCell}
                gridCell
              />
            </TableCell>
          );
        })}

      {/* #2473 — numericOnly is stamped per SHEET (score-common's
          scoreSheetSchema, from the class's paperEntry option, #2406) and
          can legitimately differ row to row within one class (a class
          re-flagged mid-day leaves earlier sheets stamped false), so this
          gates per ROW, not per column: the TextField is not rendered at
          all for a numericOnly row, while other rows in the same class keep
          theirs. That naturally covers the uniform case too (every row
          numericOnly collapses to no cell rendering anything, same visible
          effect as hiding the whole column) without a second code path.
          Not rendering it (vs. disabling it) matches AC1's "no longer
          offers ... a general-remarks box" literally, and matches how
          #2414 gated ScoringForm's own General Remarks field for the same
          flag: the field is simply absent, not present-but-locked. No
          escape hatch (AC5): there is no toggle back to editable, on this
          row or any other — scribe-fe is a kiosk. Not rendering the input
          also means it carries no `data-grid-cell` attribute, so
          focusNextGridCell (classGridNav.js) walks straight past a
          numericOnly row's notes cell the same way it already skips a
          disabled cell (see classGridNav.js's #2623 comment) — no separate
          nav-skip logic was needed here. */}
      <TableCell>
        {!sheet?.numericOnly && (
          <TextField
            size="small"
            fullWidth
            placeholder={t("classGrid.notesPlaceholder")}
            inputProps={{ "data-grid-cell": "true" }}
            value={sheet?.generalRemarks ?? ""}
            onChange={(e) => setGeneralRemarks(e.target.value)}
            onKeyDown={focusNextGridCell}
            inputRef={
              observations.length === 0 && scoredColumns.length === 0 ? registerFirst : undefined
            }
          />
        )}
      </TableCell>

      {/* Total is visually required (bold border, MUI error state while
          empty) — DR133.1.j: every rider gets a final percentage. Gait/notes
          cells above stay unstyled: the printed sheet says preliminary marks
          are NOT required.

          The empty state is ERROR RED, not amber, and that is the deliberate
          choice of the two (round-6 judge review: the earlier code asked for
          both and the comment described the one that lost). `error` and an
          sx borderColor cannot both win: MUI's
          `.MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline`
          out-specifies a plain `& .MuiOutlinedInput-notchedOutline` rule, so
          a `warning.main` here was dead code that rendered red anyway. Error
          is the one we keep because it is the only one of the two that also
          reaches assistive tech — `error` sets aria-invalid on a `required`
          input, and a missing final percentage is a real DR133.1.j defect,
          not advice. So: `error={!hasTotal}` owns the empty look, and the sx
          below only paints the success outline once a Total exists (there is
          no MUI `success` prop for that half).

          #2183 — NOT RENDERED AT ALL for a place-only class (CHAMPIONSHIP/
          SUITABILITY, DR204.2.c/DR210.5 award no scores): not merely hidden,
          there is nothing to type into and nothing to autosave. ClassGrid's
          own header omits the matching column, so the table stays aligned.

          #2184 — for a SCORED grid class (MATERIALE/UNDER_SADDLE), this is
          NOT a typed entry at all: DR211.4's Total is DERIVED from the
          row's own scored cells (scoredTotals, computed above via the same
          resolveScoreSheetTotals ScoringForm's movement grid uses), so it
          renders as a read-only computed value instead of the editable
          judge-percentage TextField below. */}
      {!isDshbPlaceOnlyGridClass && (
        <TableCell align="center">
          {isDshbScoredGridClass ? (
            <Box
              data-testid={`class-grid-computed-total-${ride.competitorNumber ?? ride.id}`}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 64,
                minHeight: 40,
                px: 1,
                border: 2,
                borderRadius: 1,
                borderColor: hasTotal ? "success.main" : "divider",
              }}
            >
              <Typography variant="body2" fontWeight={700}>
                {hasTotal && typeof scoredTotals?.percentage === "number"
                  ? `${scoredTotals.percentage.toFixed(1)}%`
                  : "—"}
              </Typography>
            </Box>
          ) : (
            <TextField
              size="small"
              type="number"
              required
              disabled={Boolean(isConfirmed)}
              error={!hasTotal}
              inputProps={{
                "data-grid-cell": "true",
                style: { width: 64, textAlign: "center" },
                "aria-label": t("classGrid.totalAriaLabel", {
                  number: ride.competitorNumber ?? "",
                }),
              }}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth: 2,
                  ...(hasTotal ? { borderColor: "success.main" } : {}),
                },
              }}
              value={totalDraft !== undefined ? totalDraft : sheet?.percentage ?? ""}
              onChange={(e) => setTotalDraft(e.target.value)}
              onBlur={(e) => commitTotal(e.target.value)}
              onKeyDown={focusNextGridCell}
            />
          )}
        </TableCell>
      )}

      {/* #2183 — Championship Class Sheet's "Prior Score" column (DR210.5):
          the qualifying-class percentage this horse/rider carried INTO the
          championship. Read-only display data only — Ride.priorScore is not
          populated by anything yet (#2185's job) and is NEVER read here or
          anywhere in placing math, only rendered when present. CHAMPIONSHIP
          only: Suitability has no such column (see ClassGrid's header). */}
      {isChampionshipGridClass && (
        <TableCell align="center" data-testid="class-grid-prior-score-cell">
          <Typography variant="body2" color="text.secondary">
            {typeof ride?.priorScore === "number" ? ride.priorScore : "—"}
          </Typography>
        </TableCell>
      )}

      {/* #1752 — Place is a REAL ENTRY CELL, not a placeholder. #1750 left it
          as a static "—" and deferred the product decision here; this is that
          decision, and it is settled by the instrument rather than by taste.

          There is no placing math to run for this family. USEF DR133.1.k /
          DR133.2.g.4 make the Dressage Seat Equitation / Medal score sheets
          mandatory, and those sheets print in bold italic: "ONLY the TOTAL
          SCORE and PLACE columns are REQUIRED to be marked for EACH rider".
          The judge writes a number in this column. So the cell gets the same
          treatment Total has — required, error-red while empty, success
          outline once filled — because the required marker in the header
          ("Place *") describes a column the judge really does have to fill.

          What this deliberately does NOT do is extend the class-completion
          gate. #1750's gate counts rows missing a TOTAL and cites DR133.1.j
          ("Judges are required to give a final percentage score to all riders
          competing in the class"), which is a rule about the percentage and
          not about the place. A judge legitimately fills the Place column last,
          after the whole class has been seen, and on a Medal class a tie may
          be waiting on a ride-off (DR133.2.g.5) before the numbers exist at
          all. Blocking confirm on Place would deadlock exactly that case.

          Writes ScoreSheet.place through the same per-row setSheetFields
          autosave as everything else on this row, so it goes through the
          offline outbox under this row's own entityKey. It does NOT write
          Ride.placing: committing the class's placings is still
          PUT /class/:classId/placings, from the Place screen. */}
      <TableCell align="center" data-testid="class-grid-place-cell">
        <TextField
          size="small"
          type="number"
          required
          disabled={Boolean(isConfirmed)}
          error={!hasPlace}
          inputProps={{
            "data-grid-cell": "true",
            min: 1,
            step: 1,
            style: { width: 56, textAlign: "center" },
            "aria-label": t("classGrid.placeAriaLabel", {
              number: ride.competitorNumber ?? "",
            }),
          }}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderWidth: 2,
              ...(hasPlace ? { borderColor: "success.main" } : {}),
            },
          }}
          value={placeDraft !== undefined ? placeDraft : sheet?.place ?? ""}
          onChange={(e) => setPlaceDraft(e.target.value)}
          onBlur={(e) => commitPlace(e.target.value)}
          onKeyDown={focusNextGridCell}
        />
        {/* #2183 — DR210.5's own terminology for a CHAMPIONSHIP class's top 3
            places ("a champion, reserve champion and third place reserve
            must be named"). Suitability places 1/2/3 stay plain numbers —
            DR204.2.c never uses this vocabulary. Purely a label alongside
            the numeric entry; the stored value is still the ordinal. */}
        {isChampionshipGridClass && CHAMPIONSHIP_PLACE_LABEL_KEYS[sheet?.place] && (
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            data-testid="class-grid-championship-place-label"
          >
            {t(CHAMPIONSHIP_PLACE_LABEL_KEYS[sheet.place])}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
}
